import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Settings, GripVertical } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import MyTasksWidget from "@/components/dashboard/widgets/MyTasksWidget";
import UpcomingTasksWidget from "@/components/dashboard/widgets/UpcomingTasksWidget";
import ProjectsWidget from "@/components/dashboard/widgets/ProjectsWidget";
import RecentActivitiesWidget from "@/components/dashboard/widgets/RecentActivitiesWidget";
import TimeTrackingWidget from "@/components/dashboard/widgets/TimeTrackingWidget";
import StatsWidget from "@/components/dashboard/widgets/StatsWidget";
import InvestorHealthWidget from "@/components/dashboard/widgets/InvestorHealthWidget";
import WidgetSelector from "@/components/dashboard/WidgetSelector";

const defaultWidgets = [
  { id: "stats", type: "stats", order: 0 },
  { id: "projects", type: "projects", order: 1 },
  { id: "myTasks", type: "myTasks", order: 2 },
  { id: "timeTracking", type: "timeTracking", order: 3 },
  { id: "activities", type: "activities", order: 4 },
];

export default function Dashboard() {
  const [showSelector, setShowSelector] = useState(false);
  const [widgets, setWidgets] = useState(defaultWidgets);
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () => base44.auth.me(),
  });

  useEffect(() => {
    if (user) {
      if (user.dashboard_config && Array.isArray(user.dashboard_config) && user.dashboard_config.length > 0) {
        setWidgets(user.dashboard_config);
      } else {
        setWidgets(defaultWidgets);
      }
    }
  }, [user]);

  const updateUserMutation = useMutation({
    mutationFn: (data) => base44.auth.updateMe(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    },
  });

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(widgets);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedWidgets = items.map((item, index) => ({ ...item, order: index }));
    setWidgets(updatedWidgets);
    updateUserMutation.mutate({ dashboard_config: updatedWidgets });
  };

  const handleToggleWidget = (type) => {
    const exists = widgets.find(w => w.type === type);
    let updatedWidgets;
    
    if (exists) {
      updatedWidgets = widgets.filter(w => w.type !== type);
    } else {
      updatedWidgets = [...widgets, { id: type, type, order: widgets.length }];
    }
    
    setWidgets(updatedWidgets);
    updateUserMutation.mutate({ dashboard_config: updatedWidgets });
  };

  const renderWidget = (widget) => {
    switch (widget.type) {
      case "stats":
        return <StatsWidget />;
      case "myTasks":
        return <MyTasksWidget />;
      case "upcomingTasks":
        return <UpcomingTasksWidget />;
      case "projects":
        return <ProjectsWidget />;
      case "activities":
        return <RecentActivitiesWidget />;
      case "timeTracking":
        return <TimeTrackingWidget />;
      case "investorHealth":
        return <InvestorHealthWidget />;
      default:
        return null;
    }
  };

  const sortedWidgets = [...widgets].sort((a, b) => a.order - b.order);

  const hour = new Date().getHours();
  const greeting = hour < 10 ? "God morgen" : hour < 18 ? "God dag" : "God kveld";
  const firstName = (user?.first_name || (user?.full_name || "").split(" ")[0] || "der");
  const today = new Date().toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Greeting hero */}
        <div className="relative overflow-hidden rounded-3xl bg-brand-ink text-white px-6 py-7 lg:px-9 lg:py-8 mb-6 animate-fade-in">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-brand-lime/20 blur-3xl" />
          <div className="absolute -right-4 -bottom-20 w-52 h-52 rounded-full bg-brand-lime/10 blur-2xl" />
          <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="text-sm text-white/50 capitalize">{today}</p>
              <h1 className="mt-1 text-2xl lg:text-3xl font-extrabold tracking-tight">
                {greeting}, {firstName}<span className="text-brand-lime">.</span>
              </h1>
              <p className="mt-1.5 text-sm text-white/60">Her er en oversikt over virksomheten din i dag.</p>
            </div>
            <Button
              onClick={() => setShowSelector(true)}
              variant="outline"
              size="sm"
              className="gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white backdrop-blur-sm w-fit"
            >
              <Settings className="w-4 h-4" />
              Tilpass dashbord
            </Button>
          </div>
        </div>

        {/* Widgets Grid */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="widgets">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="grid grid-cols-1 lg:grid-cols-2 gap-4 auto-rows-min"
              >
                {sortedWidgets.map((widget, index) => (
                  <Draggable key={widget.id} draggableId={widget.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`relative lg:col-span-2 ${snapshot.isDragging ? "z-50" : ""}`}
                      >
                        <div
                          {...provided.dragHandleProps}
                          className="absolute -top-2 -left-2 p-2 bg-white rounded-lg shadow-md cursor-move hover:bg-slate-50 transition-colors z-10 opacity-0 hover:opacity-100"
                        >
                          <GripVertical className="w-4 h-4 text-slate-400" />
                        </div>
                        {renderWidget(widget)}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {widgets.length === 0 && (
          <div className="text-center py-16">
            <p className="text-slate-500 mb-4">Ingen widgets valgt</p>
            <Button onClick={() => setShowSelector(true)} variant="outline">
              <Settings className="w-4 h-4 mr-2" />
              Velg Widgets
            </Button>
          </div>
        )}

        {/* Widget Selector */}
        {showSelector && (
          <WidgetSelector
            selectedWidgets={widgets}
            onToggle={handleToggleWidget}
            onClose={() => setShowSelector(false)}
          />
        )}
      </div>
    </div>
  );
}